import React, { memo, useId } from "react";
import PropTypes from "prop-types";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import { parseCssStringToObject, resolveInlineFontFamily } from "../../../../helper";
import { applyCatalogUpsellTextAlignStyles } from "../../../../../shared/text-block-layout.js";
import {
  formatRatingDisplayValue,
  parseRatingDisplayValue,
  resolveRatingStarFills,
} from "../../../../../shared/rating-display.js";
import { getRatingDisplay } from "../../../../../shared/product-block-display.js";

const STAR_PATH =
  "M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z";

function CustomFilledStar({ color, size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d={STAR_PATH} fill={color} />
    </svg>
  );
}

function CustomUnfilledStar({ color, size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d={STAR_PATH} stroke={color} fill="none" />
    </svg>
  );
}

function CustomPartialStar({ color, size, fillFraction, clipId }) {
  const width = 24 * Math.min(1, Math.max(0, fillFraction));
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={width} height="24" />
        </clipPath>
      </defs>
      <path d={STAR_PATH} stroke={color} fill="none" />
      <path d={STAR_PATH} fill={color} clipPath={`url(#${clipId})`} />
    </svg>
  );
}

function RatingBlock({ section, catalogFlow = false }) {
  const { isMobile } = useApp();
  const clipBase = useId();

  const baseStyles = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  const blockCSS = catalogFlow
    ? applyCatalogUpsellTextAlignStyles(baseStyles)
    : baseStyles;

  const resolvedFontFamily = resolveInlineFontFamily(blockCSS.fontFamily);
  if (resolvedFontFamily) {
    blockCSS.fontFamily = resolvedFontFamily;
  } else {
    delete blockCSS.fontFamily;
  }

  const display = getRatingDisplay(section);
  const starColor =
    display.starColor || blockCSS.color || "#f59e0b";
  const fontSize = blockCSS.fontSize || "16px";
  const starSize =
    parseInt(display.starSize || fontSize, 10) ||
    parseInt(fontSize, 10) ||
    16;
  const ratingNumeric = parseRatingDisplayValue(display.ratingValue);
  const ratingLabel = formatRatingDisplayValue(ratingNumeric);
  const starFills = resolveRatingStarFills(ratingNumeric);
  const reviewCount = display.reviewCount;
  const reviewsLabel =
    typeof reviewCount === "number"
      ? reviewCount.toLocaleString()
      : String(reviewCount || "");

  const ratingValueStyle = {
    color: display.ratingValueColor || blockCSS.color || "inherit",
    fontSize: display.ratingValueFontSize || fontSize,
  };
  const reviewCountStyle = {
    color: display.reviewCountColor || "#64748b",
    fontSize: display.reviewCountFontSize || "12px",
  };

  const renderStars = () =>
    starFills.map((fill, index) => {
      if (fill >= 1) {
        return (
          <CustomFilledStar key={index} color={starColor} size={starSize} />
        );
      }
      if (fill <= 0) {
        return (
          <CustomUnfilledStar key={index} color={starColor} size={starSize} />
        );
      }
      return (
        <CustomPartialStar
          key={index}
          color={starColor}
          size={starSize}
          fillFraction={fill}
          clipId={`${clipBase}-star-${index}`}
        />
      );
    });

  return (
    <div style={blockCSS} className="rating-block">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: catalogFlow ? "flex-start" : undefined,
          gap: 6,
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        {display.showStars !== false ? (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
            aria-hidden
          >
            {renderStars()}
          </span>
        ) : null}
        <span>
          <span style={ratingValueStyle}>{ratingLabel}</span>
          {reviewsLabel ? (
            <span style={reviewCountStyle}>{` (${reviewsLabel} reviews)`}</span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

RatingBlock.propTypes = {
  section: PropTypes.object,
  catalogFlow: PropTypes.bool,
};

RatingBlock.defaultProps = {
  section: {},
  catalogFlow: false,
};

export default memo(RatingBlock);
